import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PeriodPicker } from "@/components/PeriodPicker";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DateInput } from "@/components/ui/date-input";
import { eur, frDate } from "@/lib/format";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const SHORTCUTS = [["7", "7 j vs 7 j précédents"], ["14", "14 j vs 14 j précédents"], ["30", "30 j vs 30 j précédents"], ["month", "Mois en cours vs mois précédent"], ["n1", "Période vs même période N-1"], ["custom", "Dates personnalisées"]];

export function shortcutDates(k, base) {
  const today = new Date(); const yesterday = iso(new Date(today.getTime() - 86400000));
  if (k === "month") {
    const bS = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1)), aS = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1)), aE = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 0));
    return { a: [iso(aS), iso(aE)], b: [iso(bS), yesterday] };
  }
  if (k === "n1") {
    const b = base || [addDays(yesterday, -29), yesterday];
    return { a: [b[0].replace(/^\d{4}/, (y) => y - 1), b[1].replace(/^\d{4}/, (y) => y - 1)], b };
  }
  const n = Number(k) || 30;
  const bS = addDays(yesterday, -n + 1);
  return { a: [addDays(bS, -n), addDays(bS, -1)], b: [bS, yesterday] };
}

const empty = () => ({ period_id: "", start: "", end: "", revenue: "", customers: "", avg_ticket: "" });

function PeriodBox({ title, p, set, saved, label, testid }) {
  const pick = (id) => { const s = saved.find((x) => x.id === id); set(s ? { period_id: id, start: s.start, end: s.end, revenue: s.metrics?.revenue ?? "", customers: s.metrics?.customers ?? "", avg_ticket: s.metrics?.avg_ticket ?? "" } : { ...p, period_id: "" }); };
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2"><div className="font-semibold text-sm">{title}</div>
        {saved.length > 0 && <select value={p.period_id} onChange={(e) => pick(e.target.value)} className="text-xs border rounded-lg px-2 py-1 max-w-[55%]" data-testid={`${testid}-saved`}><option value="">Saisir / importer</option>{saved.map((s) => <option key={s.id} value={s.id}>{frDate(s.start)} → {frDate(s.end)} · {eur(s.metrics?.revenue)}</option>)}</select>}
      </div>
      {!p.period_id && <PeriodPicker value={{ start: p.start, end: p.end, preset: p.preset || "custom" }} onChange={(v) => set({ ...p, start: v.start, end: v.end, preset: v.preset })} testid={`${testid}-picker`} />}
      {p.period_id && <p className="text-xs text-slate-500" data-testid={`${testid}-range`}>{frDate(p.start)} → {frDate(p.end)}</p>}
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" inputMode="decimal" placeholder="CA €" value={p.revenue} disabled={!!p.period_id} onChange={(e) => set({ ...p, revenue: e.target.value })} className="h-11" data-testid={`${testid}-revenue`} />
        <Input type="number" inputMode="numeric" placeholder={label} value={p.customers} disabled={!!p.period_id} onChange={(e) => set({ ...p, customers: e.target.value })} className="h-11" data-testid={`${testid}-customers`} />
        <Input type="number" inputMode="decimal" placeholder="Panier €" value={p.avg_ticket} disabled={!!p.period_id} onChange={(e) => set({ ...p, avg_ticket: e.target.value })} className="h-11" data-testid={`${testid}-avg`} />
      </div>
    </div>
  );
}

export function ActivityCompare({ est, profile, saved = [], preset, onResult, onImport }) {
  const [shortcut, setShortcut] = useState("30");
  const [a, setA] = useState(empty()); const [b, setB] = useState(empty());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const label = profile?.customers_label || "Clients";

  const apply = (k, base) => {
    setShortcut(k);
    if (k === "custom") return;
    const d = shortcutDates(k, base);
    setA((p) => ({ ...empty(), start: d.a[0], end: d.a[1] })); setB((p) => ({ ...empty(), start: d.b[0], end: d.b[1] }));
  };
  useEffect(() => { if (preset) { setA({ period_id: preset.id, start: preset.start, end: preset.end, revenue: preset.metrics?.revenue ?? "", customers: preset.metrics?.customers ?? "", avg_ticket: preset.metrics?.avg_ticket ?? "" }); setShortcut("custom"); } else apply("30"); }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps

  const num = (v) => (v === "" || v == null ? null : Number(v));
  const body = (p) => (p.period_id ? { period_id: p.period_id } : { start: p.start, end: p.end, revenue: num(p.revenue), customers: num(p.customers), avg_ticket: num(p.avg_ticket) });
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/establishments/${est.id}/activity/compare`, { a: body(a), b: body(b), user_note: note || null, shortcut }); onResult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const ready = (p) => p.period_id || (p.start && p.end && (p.revenue !== "" || p.customers !== ""));
  return (
    <div className="space-y-4" data-testid="activity-compare-form">
      <div className="flex flex-wrap gap-2">{SHORTCUTS.map(([k, l]) => <Button key={k} size="sm" variant={shortcut === k ? "default" : "outline"} onClick={() => apply(k, shortcut === "n1" ? null : [b.start, b.end])} className="rounded-full h-8 text-xs" data-testid={`compare-shortcut-${k}`}>{l}</Button>)}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PeriodBox title="Période A (référence)" p={a} set={setA} saved={saved} label={label} testid="compare-a" />
        <PeriodBox title="Période B (à analyser)" p={b} set={setB} saved={saved} label={label} testid="compare-b" />
      </div>
      <p className="text-xs text-slate-400">Les chiffres peuvent aussi provenir d'une capture ou d'un document : <button className="underline" onClick={onImport} data-testid="compare-import-link">importer d'abord</button>, puis choisir la période enregistrée ci-dessus.</p>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Mon contexte — ajoutez un élément que Respondia ne peut pas connaître : fermeture exceptionnelle, travaux, changement d'horaires, promotion, événement, absence d'un salarié, nouvelle offre…" data-testid="compare-note" />
      <Button onClick={submit} disabled={busy || !ready(a) || !ready(b)} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 rounded-xl h-11" data-testid="compare-submit">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparaison…</> : "Comparer"}</Button>
    </div>
  );
}
